"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowUpRight, Wrench, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface FaqItem {
  id: string;
  number: string;
  industry: string;
  question: string;
  answer: string;
  highlights: string[];
}
const faqData: FaqItem[] = [{
  id: "item-1",
  number: "01",
  industry: "Pasta e Papel",
  question: "Como é aplicada a supressão de poeiras no corte e bobinagem de papel?",
  answer: "As unidades de produção de pasta podem gerar poeiras durante o manuseamento de madeira, a transferência de aparas, o processamento de biomassa e a movimentação de materiais. A acumulação de poeiras pode criar desafios de manutenção, reduzir a visibilidade e contribuir para preocupações de manutenção em torno de correias transportadoras, pontos de transferência e equipamentos de processamento.\nA Spraying Systems Co. ajuda os produtores de pasta a aplicar pulverizações de controlo de poeiras onde as partículas são geradas, utilizando bicos e sistemas de pulverização selecionados para a disposição do processo, o tipo de material e a qualidade da água disponível. O posicionamento adequado da pulverização e o tamanho de gota ajudam a suprimir as poeiras sem humedecer excessivamente as aparas, as fibras ou o equipamento envolvente, apoiando uma operação mais limpa e um manuseamento de materiais mais fiável.",
  highlights: ["Bicos de atomização a ar com jato plano", "Controlo automático de caudal e pressão", "Supressão sem molhagem residual"]
}, {
  id: "item-2",
  number: "02",
  industry: "Metais",
  question: "De que forma atuam os sistemas em fundições e siderurgias?",
  answer: "As operações de produção de ferro e aço geram poeiras no manuseamento de matérias-primas, no carregamento, no manuseamento de escória, em correias transportadoras, pontos de transferência e áreas de processo de elevada temperatura. Estes ambientes podem ser severos, com materiais abrasivos, calor, equipamentos pesados e condições de operação variáveis que tornam o controlo de poeiras difícil.\nA Spraying Systems Co. ajuda os produtores de aço a selecionar bicos robustos, cabeçotes e sistemas de pulverização automatizados para a supressão direcionada de poeiras. O tamanho de gota e o posicionamento corretos ajudam a controlar as poeiras em suspensão, minimizando o excesso de água que poderia afetar o manuseamento de materiais ou o processamento a jusante. As soluções de pulverização de engenharia apoiam áreas de trabalho mais limpas, melhor visibilidade e menores exigências de manutenção.",
  highlights: ["Resistência a temperaturas extremas", "Gotas calibradas para captura PM10 e PM2.5", "Manutenção preventiva simplificada"]
}, {
  id: "item-3",
  number: "03",
  industry: "PRETÓLEO E GÁS",
  question: "Quais são as soluções para zonas de carga e processamento petroquímico?",
  answer: "As operações upstream de petróleo e gás podem exigir controlo de poeiras em torno do manuseamento de areia, áreas de apoio à perfuração, pilhas de armazenamento, carregamento de camiões e pontos de transferência. As poeiras podem afetar a visibilidade dos trabalhadores, a fiabilidade dos equipamentos e a manutenção do local, especialmente em áreas de operação remotas ou de elevado tráfego. A Spraying Systems Co. ajuda as operações upstream a configurar soluções de supressão de poeiras utilizando bicos, barras de pulverização e controlos adequados à fonte de poeiras e ao ambiente de operação. A aplicação adequada de pulverização ajuda a reduzir as partículas em suspensão sem consumo excessivo de água, apoiando condições de local mais seguras, equipamentos mais limpos e um manuseamento mais fiável de materiais a granel com poeiras.",
  highlights: ["Certificação para zonas classificadas", "Lanças e manifolds em ligas especiais", "Redução de emissões fugitivas"]
}, {
  id: "item-4",
  number: "04",
  industry: "Engenharia de Madeiras",
  question: "Como mitigar o risco de pó combustível em serrações e painéis MDF?",
  answer: "As operações de madeira de engenharia podem gerar poeiras durante o manuseamento de madeira, o processamento de fibras, a formação de mantas, a lixagem, o corte e a transferência de materiais. As poeiras finas de madeira podem criar desafios de manutenção, acumulação em equipamentos, preocupações de visibilidade e riscos de segurança ao longo do processo de produção.\nA Spraying Systems Co. ajuda os fabricantes de madeira de engenharia a controlar as poeiras com bicos e sistemas de pulverização concebidos para aplicação direcionada em áreas de manuseamento e processamento de materiais. O tamanho de gota, o posicionamento e o controlo de caudal adequados ajudam a suprimir as poeiras sem adicionar demasiada humidade à fibra de madeira ou aos materiais acabados. Estas soluções apoiam áreas de produção mais limpas e uma operação mais fiável dos equipamentos.",
  highlights: ["Minimização de riscos de combustão", "Precipitação de serradura e aparas", "Integração com sensores de caudal"]
}, {
  id: "item-5",
  number: "05",
  industry: "Minas e Pedreiras",
  question: "Como conter poeiras em britadeiras, crivos e frentes de extração?",
  answer: "As operações mineiras dependem de um controlo eficaz de poeiras em torno de trituradores, correias transportadoras, pontos de transferência, vias de transporte, pilhas de armazenamento e áreas de carregamento. As poeiras podem reduzir a visibilidade, aumentar a manutenção, afetar a segurança dos trabalhadores e criar desafios de gestão do local em ambientes de operação severos.\nA Spraying Systems Co. ajuda as operações mineiras a controlar as poeiras na origem com bicos robustos, cabeçotes de pulverização, sistemas automatizados e expertise de aplicação. A seleção adequada de pulverização ajuda a combinar o tamanho de gota com as partículas de poeira, utilizando água de forma eficiente. A supressão direcionada de poeiras ajuda a melhorar a visibilidade, a reduzir as partículas em suspensão e a apoiar um manuseamento de materiais a granel mais seguro e limpo.",
  highlights: ["Sistemas robustos de alto rendimento", "Consumo eficiente de água industrial", "Bicos anti-entupimento de longa vida útil"]
}, {
  id: "item-6",
  number: "06",
  industry: "Cimento",
  question: "Qual o método para estancamento de pós no manuseamento de clínquer?",
  answer: "O clínquer e os aditivos cimentícios geram partículas abrasivas e densas. ",
  highlights: ["Materiais com tratamento anti-abrasão", "Controlo proporcional sincronizado com tapetes", "Total integração nas linhas existentes"]
}];
export default function DustFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="dustcontrolindustryfaq" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28">
      {/* Ambient background decoration */}
      <div className="pointer-events-none absolute -top-24 -left-24 size-96 rounded-full bg-accent opacity-50 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 size-96 rounded-full bg-secondary/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column: Context & Support Card */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <motion.div initial={{
              opacity: 0,
              y: shouldReduceMotion ? 0 : 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="space-y-6">
                <Badge variant="outline" className="gap-1.5 border-border bg-card px-3 py-1 text-xs font-semibold text-primary">
                  <HelpCircle className="size-3.5 text-secondary" />
                  <span>Aplicações Industriais</span>
                </Badge>

                <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                  Controlo de Poeiras por Indústria
                </h2>

                <p className="font-sans text-base leading-relaxed text-muted-foreground">
                  Desde 1993, a Eurocontrol desenvolve e instala sistemas de engenharia de pulverização à medida para supressão, captura e confinamento de poeiras, garantindo máxima conformidade ambiental e eficiência operacional em cada setor.
                </p>

                {/* Technical Support Card */}
                <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                      <Wrench className="size-5" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-default text-base font-semibold text-card-foreground">
                        Necessita de suporte técnico específico?
                      </h3>
                      <p className="font-sans text-sm text-muted-foreground">
                        Os nossos engenheiros analisam os seus processos industriais e projetam a solução de pulverização ideal para a sua fábrica.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 pt-5 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <ShieldCheck className="size-4 text-secondary" />
                      <span>Representante Spraying Systems Co.</span>
                    </div>

                    <Button asChild size="sm" variant="default" className="w-full sm:w-auto">
                      <Link to="/ContactsPage" className="gap-1.5 font-medium">
                        <span>Consulte-nos</span>
                        <ArrowUpRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Right Column: Industrial Accordion */}
          <div className="lg:col-span-7">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.1
          }} className="space-y-4">
              <Accordion type="single" collapsible className="w-full space-y-4">
                {faqData.map((item, index) => <motion.div key={item.id} data-index={index} variants={itemVariants}>
                    <AccordionItem value={item.id} className="group rounded-xl border border-border bg-card px-5 py-1 shadow-xs transition-colors hover:border-secondary/50">
                      <AccordionTrigger className="py-4 text-left hover:no-underline [&[data-state=open]>div>span]:bg-primary [&[data-state=open]>div>span]:text-primary-foreground">
                        <div className="flex items-center gap-4 text-left">
                          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted font-mono text-xs font-bold text-muted-foreground transition-colors group-hover:bg-accent group-hover:text-accent-foreground">
                            {item.number}
                          </span>
                          <div className="space-y-1">
                            <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                              {item.industry}
                            </span>
                            <p className="font-default text-base font-medium text-card-foreground">
                              {item.question}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="pt-2 pb-5">
                        <div className="pl-11 space-y-4">
                          <p className="font-sans text-sm leading-relaxed text-muted-foreground">
                            {item.answer}
                          </p>

                          <div className="flex flex-wrap gap-2 pt-2 border-t border-border/60">
                            {item.highlights.map((highlight, hIndex) => <div key={hIndex} data-index={hIndex} className="inline-flex items-center gap-1.5 rounded-md bg-muted/60 px-2.5 py-1 text-xs font-medium text-foreground">
                                <Sparkles className="size-3 text-secondary" />
                                <span>{highlight}</span>
                              </div>)}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>)}
              </Accordion>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}